import React, { useEffect, useState, useMemo, memo } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "./firebaseConfig";
import "./ButtonStyles.css"; // For consistent button styles
import { useNavigate } from "react-router-dom";

// QuizCard component for rendering individual quizzes
const QuizCard = memo(({ quiz, user, index, onLock, onUnlock, onDelete, onViewResults }) => {
  // Styles for the quiz card
  const tableBodyCellStyle = {
    padding: "14px 16px",
    borderBottom: "1px solid #c2dcf2",
    fontSize: "15px",
    verticalAlign: "middle",
  };

  const actionButtonsContainerStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  };

  const baseActionButtonStyle = {
    padding: "8px 14px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "14px",
    transition: "all 0.2s",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const lockBtnStyle = {
    ...baseActionButtonStyle,
    backgroundColor: "#ffebcc",
    color: "#b26200",
    boxShadow: "0 2px 4px rgba(255, 152, 0, 0.2)",
  };

  const unlockBtnStyle = {
    ...baseActionButtonStyle,
    backgroundColor: "#c2dbff",
    color: "#0b5cad",
    boxShadow: "0 2px 4px rgba(66, 133, 244, 0.2)",
  };

  const deleteBtnStyle = {
    ...baseActionButtonStyle,
    backgroundColor: "#ffd5d5",
    color: "#b52b27",
    boxShadow: "0 2px 4px rgba(217, 83, 79, 0.2)",
  };

  const statusBadgeStyle = (locked) => ({
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "50px",
    fontSize: "13px",
    fontWeight: "500",
    backgroundColor: locked ? "#ffd5d5" : "#c2dcf2",
    color: locked ? "#b52b27" : "#2980b9",
    marginLeft: "10px",
  });

  return (
    <tr
      style={{
        backgroundColor: index % 2 === 0 ? "#ffffff" : "#f2f7fc",
      }}
    >
      <td style={tableBodyCellStyle}>
        <span 
          style={{ 
            fontWeight: "500", 
            cursor: "pointer", 
            color: "#2980b9",
            textDecoration: "underline"
          }}
          onClick={() => onViewResults(quiz.quizCode)}
        >
          {quiz.quizCode}
        </span>
      </td>
      <td style={tableBodyCellStyle}>
        {quiz.createdAt
          ? new Date(quiz.createdAt.seconds * 1000).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "N/A"}
      </td>
      <td style={tableBodyCellStyle}>
        <span style={statusBadgeStyle(quiz.locked)}>
          {quiz.locked ? "Locked" : "Active"}
        </span>
      </td>
      <td style={tableBodyCellStyle}>
        {(user?.role === "admin" || user?.role === "teacher") && (
          <div style={actionButtonsContainerStyle}>
            {quiz.locked ? (
              <button
                onClick={() => onUnlock(quiz.id)}
                style={unlockBtnStyle}
              >
                Unlock Quiz
              </button>
            ) : (
              <button
                onClick={() => onLock(quiz.id)}
                style={lockBtnStyle}
              >
                Lock Quiz
              </button>
            )}
            {(user?.role === "admin" || user?.role === "teacher") && (
              <button
                onClick={() => onDelete(quiz.id)}
                style={deleteBtnStyle}
              >
                Delete Quiz
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
});

const ViewQuizzes = ({ user }) => {
  const [quizzes, setQuizzes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isHoveringBack, setIsHoveringBack] = useState(false);
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  // Redirect non-admin/teacher users to dashboard
  useEffect(() => {
    if (user && user.role !== "admin" && user.role !== "teacher") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Fetch quizzes from Firestore with role-based filtering
  useEffect(() => {
    const fetchQuizzes = async () => {
      if (!user) return; // Don't fetch if user is not available
      
      setIsLoading(true);
      try {
        let quizzesQuery;
        
        // Role-based filtering
        if (user.role === "admin") {
          // Admins see all quizzes
          quizzesQuery = collection(db, "quizzes");
        } else if (user.role === "teacher") {
          // Teachers only see quizzes they created
          quizzesQuery = query(
            collection(db, "quizzes"),
            where("creatorUid", "==", user.uid)
          );
        } else {
          // Other roles shouldn't see any quizzes (they'll be redirected)
          return;
        }
        
        const querySnapshot = await getDocs(quizzesQuery);
        // Map over each quiz doc
        const fetchedQuizzes = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setQuizzes(fetchedQuizzes);
        
        // Initialize expanded state for each subject
        const subjects = [...new Set(fetchedQuizzes.map(quiz => quiz.subject || "Uncategorized"))];
        const initialExpandedState = {};
        subjects.forEach(subject => {
          initialExpandedState[subject] = false; // All collapsed initially
        });
        setExpandedSubjects(initialExpandedState);
      } catch (error) {
        console.error("Error fetching quizzes: ", error.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchQuizzes();
  }, [user, navigate]);

  // Group quizzes by subject
  const quizzesBySubject = useMemo(() => {
    if (!quizzes.length) return {};
    
    // Filter quizzes by search term if provided
    const filteredQuizzes = searchTerm 
      ? quizzes.filter(quiz => 
          quiz.quizCode.toLowerCase().includes(searchTerm.toLowerCase()))
      : quizzes;
    
    // Group by subject
    return filteredQuizzes.reduce((acc, quiz) => {
      const subject = quiz.subject || "Uncategorized";
      if (!acc[subject]) {
        acc[subject] = [];
      }
      acc[subject].push(quiz);
      return acc;
    }, {});
  }, [quizzes, searchTerm]);

  // Toggle subject expansion
  const toggleSubject = (subject) => {
    setExpandedSubjects(prev => ({
      ...prev,
      [subject]: !prev[subject]
    }));
  };

  // Handle delete quiz (only available to admins)
  const handleDeleteQuiz = async (quizId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this quiz? This action cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "quizzes", quizId));
      setQuizzes((prev) => prev.filter((quiz) => quiz.id !== quizId));
      alert("Quiz deleted successfully!");
    } catch (error) {
      console.error("Error deleting quiz: ", error.message);
      alert("Failed to delete quiz. Please try again.");
    }
  };

  // Handle lock quiz
  const handleLockQuiz = async (quizId) => {
    try {
      await updateDoc(doc(db, "quizzes", quizId), { locked: true });
      setQuizzes((prev) =>
        prev.map((quiz) => (quiz.id === quizId ? { ...quiz, locked: true } : quiz))
      );
      alert("Quiz locked successfully!");
    } catch (error) {
      console.error("Error locking quiz:", error.message);
      alert("Failed to lock quiz. Please try again.");
    }
  };

  // Handle unlock quiz
  const handleUnlockQuiz = async (quizId) => {
    try {
      await updateDoc(doc(db, "quizzes", quizId), { locked: false });
      setQuizzes((prev) =>
        prev.map((quiz) => (quiz.id === quizId ? { ...quiz, locked: false } : quiz))
      );
      alert("Quiz unlocked successfully!");
    } catch (error) {
      console.error("Error unlocking quiz:", error.message);
      alert("Failed to unlock quiz. Please try again.");
    }
  };

  // ---------- STYLES ----------
  const containerStyle = {
    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    minHeight: "100vh",
    padding: "20px",
    fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  };

  const tileStyle = {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: "16px",
    padding: "30px 40px",
    margin: "30px auto",
    maxWidth: "1200px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
  };

  const searchContainerStyle = {
    marginBottom: "25px",
    display: "flex",
    alignItems: "center",
  };

  const searchInputStyle = {
    flex: "1",
    padding: "12px 16px",
    borderRadius: "8px",
    border: "1px solid #c2dcf2",
    fontSize: "16px",
    outline: "none",
    transition: "border 0.2s",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
  };

  const subjectHeaderStyle = {
    backgroundColor: "#f2f7fc",
    padding: "15px 20px",
    borderRadius: "10px",
    marginBottom: "15px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
    transition: "background-color 0.2s",
  };

  const subjectNameStyle = {
    margin: 0,
    color: "#2980b9",
    fontSize: "18px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  };

  const subjectCountStyle = {
    backgroundColor: "#2980b9",
    color: "white",
    borderRadius: "50px",
    padding: "3px 10px",
    fontSize: "14px",
    fontWeight: "500",
  };

  const chevronStyle = (isExpanded) => ({
    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.3s ease",
    fontSize: "20px",
  });

  const subjectContentStyle = (isExpanded) => ({
    display: isExpanded ? "block" : "none",
    marginBottom: "25px",
  });

  const headerStyle = {
    position: "relative",
    marginBottom: "30px",
    padding: "15px 0",
    borderBottom: "2px solid #c2dcf2",
    textAlign: "center",
  };

  const titleStyle = {
    margin: 0,
    color: "#2980b9",
    fontSize: "32px",
    fontWeight: "600",
    letterSpacing: "0.5px",
  };

  const tableContainerStyle = {
    marginTop: "25px",
    overflowX: "auto",
    borderRadius: "12px",
    boxShadow: "0 6px 12px rgba(0, 0, 0, 0.08)",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0",
    overflow: "hidden",
    borderRadius: "12px",
  };

  const tableHeadStyle = {
    backgroundColor: "#2980b9",
    color: "#fff",
  };

  const tableHeadCellStyle = {
    padding: "16px",
    textAlign: "left",
    fontWeight: "600",
    letterSpacing: "0.5px",
    fontSize: "16px",
    borderBottom: "none",
  };

  const tableBodyCellStyle = {
    padding: "14px 16px",
    borderBottom: "1px solid #c2dcf2",
    fontSize: "15px",
    verticalAlign: "middle",
  };

  const actionButtonsContainerStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  };

  const baseActionButtonStyle = {
    padding: "8px 14px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "14px",
    transition: "all 0.2s",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const lockBtnStyle = {
    ...baseActionButtonStyle,
    backgroundColor: "#ffebcc",
    color: "#b26200",
    boxShadow: "0 2px 4px rgba(255, 152, 0, 0.2)",
  };

  const unlockBtnStyle = {
    ...baseActionButtonStyle,
    backgroundColor: "#c2dbff",
    color: "#0b5cad",
    boxShadow: "0 2px 4px rgba(66, 133, 244, 0.2)",
  };

  const deleteBtnStyle = {
    ...baseActionButtonStyle,
    backgroundColor: "#ffd5d5",
    color: "#b52b27",
    boxShadow: "0 2px 4px rgba(217, 83, 79, 0.2)",
  };

  // Make the back button a gradient and add a hover effect
  const backButtonContainerStyle = {
    marginTop: "30px",
    textAlign: "center",
  };

  const backButtonStyle = {
    padding: "12px 24px",
    // Gradient background
    background: "linear-gradient(45deg, #2980b9, #3498db)",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "500",
    transition: "all 0.2s",
    // Slight lift on hover
    transform: isHoveringBack ? "translateY(-2px)" : "none",
    boxShadow: isHoveringBack
      ? "0 6px 12px rgba(41, 128, 185, 0.3)"
      : "0 4px 8px rgba(41, 128, 185, 0.2)",
  };

  const emptyQuizzesStyle = {
    padding: "30px 0",
    fontSize: "16px",
    color: "#666",
    textAlign: "center",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    border: "1px dashed #ddd",
    margin: "20px 0",
  };

  const loadingStyle = {
    textAlign: "center",
    padding: "40px 0",
    color: "#2980b9",
    fontSize: "18px",
  };

  const statusBadgeStyle = (locked) => ({
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "50px",
    fontSize: "13px",
    fontWeight: "500",
    backgroundColor: locked ? "#ffd5d5" : "#c2dcf2",
    color: locked ? "#b52b27" : "#2980b9",
    marginLeft: "10px",
  });

  return (
    <div style={containerStyle}>
      <div style={tileStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>View Quizzes</h1>
        </div>

        {/* Search Bar */}
        <div style={searchContainerStyle}>
          <input
            type="text"
            placeholder="Search quizzes by code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>

        {/* Quizzes by Subject */}
        {isLoading ? (
          <div style={loadingStyle}>Loading quizzes...</div>
        ) : user && (user.role === "admin" || user.role === "teacher") && Object.keys(quizzesBySubject).length > 0 ? (
          <>
            {Object.entries(quizzesBySubject).map(([subject, subjectQuizzes]) => (
              <div key={subject}>
                {/* Subject Header */}
                <div 
                  style={subjectHeaderStyle} 
                  onClick={() => toggleSubject(subject)}
                >
                  <h3 style={subjectNameStyle}>
                    {subject}
                    <span style={subjectCountStyle}>{subjectQuizzes.length}</span>
                  </h3>
                  <span style={chevronStyle(expandedSubjects[subject])}>▼</span>
                </div>
                
                {/* Subject Content */}
                <div style={subjectContentStyle(expandedSubjects[subject])}>
                  <div style={tableContainerStyle}>
                    <table style={tableStyle}>
                      <thead style={tableHeadStyle}>
                        <tr>
                          <th style={tableHeadCellStyle}>Quiz Code</th>
                          <th style={tableHeadCellStyle}>Date Created</th>
                          <th style={tableHeadCellStyle}>Status</th>
                          <th style={tableHeadCellStyle}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subjectQuizzes.map((quiz, idx) => (
                          <QuizCard
                            key={quiz.id}
                            quiz={quiz}
                            user={user}
                            index={idx}
                            onLock={handleLockQuiz}
                            onUnlock={handleUnlockQuiz}
                            onDelete={handleDeleteQuiz}
                            onViewResults={(quizCode) => navigate(`/quiz-results/${quizCode}`)}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <div style={emptyQuizzesStyle}>
            {searchTerm 
              ? "No quizzes match your search criteria." 
              : user && user.role === "teacher"
                ? "You haven't created any quizzes yet. Create quizzes to see them here."
                : "No quizzes available. Create quizzes to see them here."}
          </div>
        )}

        {/* Back to Dashboard Button */}
        <div style={backButtonContainerStyle}>
          <button
            onClick={() => navigate("/dashboard")}
            style={backButtonStyle}
            onMouseEnter={() => setIsHoveringBack(true)}
            onMouseLeave={() => setIsHoveringBack(false)}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewQuizzes;
